import { staticRoutes } from '@/api/apiRoutes';
import { get } from '@/api/http';
import {
  IRegionCam,
  IRegionFAQ,
  IRegionFeaturedResort, IRegionResortsNonPaginated,
  IRegionResortsPaginated, IRegionStats,
  IRelatedForRegion,
} from '@/api/region/types';
import { INews, IResortTopRated, ResortTopRated } from '@/api/home/types';
import {
  IPaginationResponse, IResortPathInfo, OpenFlag, ResortStatus,
} from '@/api/resort/types';
import { IGridSorting, SortingType } from '@/components/common/Grid/types';
import { ILiftTicketsResponse } from '@/components/region/LiftTicketTable/types';
import { getPortalOptions } from '@/config/getPortalConfig';

export const fetchRegionResortsByStatus = (
  regionId: string,
  status: OpenFlag | ResortStatus,
  page: number,
  limit: number,
  orderBy?: string,
  direction?: SortingType,
) => (
  get<IRegionResortsPaginated>(
    staticRoutes.regionResortsByStatus(regionId, status, page, limit, orderBy, direction),
  )
);

export const fetchRegionLiftTicketsPricesByResort = (
  regionId: string,
  page: number,
  limit: number,
  orderBy?: string,
  direction?: SortingType,
) => (
  get<ILiftTicketsResponse>(
    staticRoutes.regionLiftTicketsPricesByResort(regionId, page, limit, orderBy, direction),
  )
);

export const fetchAllRegionResortsTopSnow = (sorting?: IGridSorting) => (
  get<IRegionResortsNonPaginated>(staticRoutes.regionAllResortsTopSnow(sorting))
);

export const fetchRegionClosingMeta = (uuid: string) => (
  get<{ 'opening': string, 'closing': string }>(staticRoutes.regionClosingOpenDates(uuid))
);

export const fetchRegionClosingDate = async (uuid: string) => (await fetchRegionClosingMeta(uuid)).closing;

export const fetchRegionOpeningDate = async (uuid: string) => (await fetchRegionClosingMeta(uuid)).opening;

export const fetchRegionMeta = (uuid: string) => (
  get<{ 'resorts':{ 'active':number } }>(staticRoutes.regionMeta(uuid))
);

export const fetchResortMeta = (uuid: string) => (
  get<{ 'snow':{ 'last24':number } }>(staticRoutes.resortMeta(uuid))
);

export const fetchRegionRelated = (regionId: string) => (
  get<IRelatedForRegion>(staticRoutes.regionRelated(regionId))
);

export const fetchRegionFeaturedCam = (regionId: string) => (
  get<IRegionCam>(staticRoutes.regionFeaturedCam(regionId))
);

export const fetchRegionCameras = async (regionId: string) => (
  await get<{ data: Array<IRegionCam>, pagination: IPaginationResponse }>(staticRoutes.regionCameras(regionId))
).data;

export const fetchRegionCamerasByStatus = (regionId: string, status: OpenFlag | ResortStatus, page: number, limit: number) => (
  get<{ data: Array<IRegionCam>, pagination: IPaginationResponse }>(
    staticRoutes.regionCamerasByStatus(regionId, status, page, limit),
  )
);

export const fetchRegionFeatured = (regionId: string) => (
  get<IRegionFeaturedResort>(staticRoutes.regionFeatured(regionId))
);

export const fetchRegionResortTopRating = async (uuid: string, type: ResortTopRated) => (
  await get<{ data: Array<IResortTopRated> }>(
    staticRoutes.regionResortTopRating(uuid, type),
    type,
  )
).data;

export const fetchRegionResortListGuide = (regionId: string) => (
  get<Array<IResortPathInfo>>(staticRoutes.regionResortListGuide(regionId))
);

export const fetchRegionFAQ = (regionId: string) => (
  get<IRegionFAQ>(staticRoutes.regionFAQ(regionId))
);

export const fetchRegionStats = (regionId: string) => (
  get<IRegionStats>(staticRoutes.regionStats(regionId))
);

export const fetchRegionRelatedNews = async (regionUUID: string) => {
  const { hasNews } = getPortalOptions();
  if (hasNews) {
    return get<Array<INews>>(staticRoutes.regionRelatedNews(regionUUID));
  }
  return null;
};
